import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchCampaignBudgetSummary } from '@/features/instruments/publish/campaign-budget/api/campaign-budget.api'
import type { CampaignInstrument } from '../types'

export function estimatedReactivationCost(instrument: CampaignInstrument): number {
  const target = instrument.responseTarget ?? 0
  const reward = instrument.rewardPerResponse ?? 0
  const count = instrument.responseCount ?? 0
  if (target > 0 && reward > 0) {
    const remaining = Math.max(0, target - count)
    return remaining * reward
  }
  const allocated = instrument.allocatedBudgetUsd ?? 0
  if (allocated > 0) return allocated
  return 0
}

type PendingReactivate = {
  instrument: CampaignInstrument
  onAllowed: () => void
}

type UseReactivateBudgetGateOptions = {
  onCheckFailed?: (message: string) => void
}

export function useReactivateBudgetGate(options?: UseReactivateBudgetGateOptions) {
  const [insufficientOpen, setInsufficientOpen] = useState(false)
  const [topUpUrl, setTopUpUrl] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const [awaitingTopUpReturn, setAwaitingTopUpReturn] = useState(false)
  const pendingRef = useRef<PendingReactivate | null>(null)

  const runBudgetCheck = useCallback(
    async (
      instrument: CampaignInstrument,
      onAllowed: () => void,
      onInsufficient: () => void,
    ): Promise<boolean> => {
      const estimatedCost = estimatedReactivationCost(instrument)
      if (estimatedCost <= 0) {
        onAllowed()
        return true
      }
      setChecking(true)
      try {
        const summary = await fetchCampaignBudgetSummary({
          instrumentId: instrument.id,
          estimatedCost,
        })
        if (!summary.walletSufficient) {
          setTopUpUrl(summary.topUpUrl ?? null)
          onInsufficient()
          return false
        }
        onAllowed()
        return true
      } catch {
        options?.onCheckFailed?.(
          'Unable to verify your Tremendous account balance. Please try again.',
        )
        return false
      } finally {
        setChecking(false)
      }
    },
    [options],
  )

  const checkBudgetThen = useCallback(
    async (instrument: CampaignInstrument, onAllowed: () => void) => {
      pendingRef.current = { instrument, onAllowed }
      setAwaitingTopUpReturn(false)
      await runBudgetCheck(instrument, onAllowed, () => setInsufficientOpen(true))
    },
    [runBudgetCheck],
  )

  const openTopUp = useCallback(() => {
    const url = topUpUrl?.trim()
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    setInsufficientOpen(false)
    setAwaitingTopUpReturn(true)
  }, [topUpUrl])

  useEffect(() => {
    if (!awaitingTopUpReturn) return

    const recheckAfterTopUp = () => {
      const pending = pendingRef.current
      if (!pending) return
      void runBudgetCheck(
        pending.instrument,
        () => {
          setAwaitingTopUpReturn(false)
          pending.onAllowed()
        },
        () => {
          setInsufficientOpen(true)
          setAwaitingTopUpReturn(false)
        },
      )
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        recheckAfterTopUp()
      }
    }

    window.addEventListener('focus', recheckAfterTopUp)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', recheckAfterTopUp)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [awaitingTopUpReturn, runBudgetCheck])

  return {
    checking,
    insufficientOpen,
    setInsufficientOpen,
    openTopUp,
    checkBudgetThen,
  }
}
